// 遠端協助的純邏輯（SPEC #kinio-kms-remote「操作者流程」「找到對的電腦」、#kinio-kms-api 錯誤碼；k1-wbs M4-06）：
// 權限勾選、這台能不能發起、錯誤 / 結束原因 → 白話結果與下一步、等待允許的倒數、加權限等待中、檔案清單、紀錄 CSV。
import { isKinioError } from '@kinio/web-sdk';
import type { HostRow } from '../types';
import { REMOTE_PERMS, type AssistRequest, type RemoteEvent, type RemotePerm, type RemoteSession } from './types';

type TFn = (k: string, o?: Record<string, unknown>) => string;

/** 發起時的預設權限：看 + 控制（SPEC「操作者流程」）；檔案傳輸與剪貼簿預設關、要另勾。 */
export const DEFAULT_PERMS: RemotePerm[] = ['view', 'control'];

/** 同意視窗 60 秒倒數（逾時 = 拒絕）；加權限 / 延長也是 60 秒。 */
export const CONSENT_SECONDS = 60;

/** 叫起遠端工具後多久沒看到 kmgr 的 launched 事件就當作這台電腦沒裝（Claude 推論，M4-06）。 */
export const LAUNCH_DETECT_MS = 8_000;

/** launch code 120 秒、一次性（SPEC「叫起 kmgrctl」）：超過就要重新發起。 */
export const LAUNCH_CODE_MS = 120_000;

/** 每次延長 30 分鐘、從開始算總長 ≤ 4 小時（SPEC #kinio-kms-remote-ticket「時間」）。 */
export const EXTEND_MINUTES = 30;
export const MAX_TOTAL_MS = 4 * 3600_000;

/** 權限照固定順序、看一定在。 */
export function normalizePerms(perms: Iterable<string>): RemotePerm[] {
  const set = new Set<string>(perms);
  set.add('view');
  return REMOTE_PERMS.filter((p) => set.has(p));
}

export function togglePerm(perms: RemotePerm[], p: RemotePerm, on: boolean): RemotePerm[] {
  if (p === 'view') return normalizePerms(perms);
  return normalizePerms(on ? [...perms, p] : perms.filter((x) => x !== p));
}

/** 還能加的權限（升級用）。 */
export function addablePerms(current: RemotePerm[]): RemotePerm[] {
  return REMOTE_PERMS.filter((p) => p !== 'view' && !current.includes(p));
}

/**
 * 這台現在不能發起的原因（按鈕停用並寫原因）；可以 → null。
 * 未授權、已停用 / 撤銷、不在線、在中國大陸、本機允許範圍沒開遠端（主機欄位 kmgr 有帶才判，沒帶交給 kmgr / Agent 判）。
 */
export type HostBlock = 'unlicensed' | 'inactive' | 'offline' | 'region' | 'allowance' | 'local_paused';

export function hostBlock(h: Pick<HostRow, 'license_state' | 'state' | 'presence' | 'cn_mainland' | 'remote_mode' | 'local_paused'>): HostBlock | null {
  if (h.state !== 'active') return 'inactive';
  if (h.license_state === 'unlicensed') return 'unlicensed';
  if (h.cn_mainland) return 'region';
  if (h.remote_mode === 'off') return 'allowance';
  if (h.local_paused) return 'local_paused';
  if (h.presence !== 'online') return 'offline';
  return null;
}

/** 〈姓名〉一律是主要使用者，沒設時寫「螢幕前的人」（不顯示本機帳號）。 */
export function screenPerson(t: TFn, h: Pick<HostRow, 'primary_user'> | undefined): string {
  return h?.primary_user?.trim() || t('remote.screen_person');
}

/** 求助碼 482915 → 「482 915」。 */
export function fmtCode(code: string): string {
  const c = code.replace(/\s+/g, '');
  return c.length === 6 ? `${c.slice(0, 3)} ${c.slice(3)}` : c;
}

/** 從協助請求發起時理由預帶求助碼與問題。 */
export function assistReason(t: TFn, a: Pick<AssistRequest, 'code' | 'note'>): string {
  return a.note ? t('remote.reason_assist_note', { code: fmtCode(a.code), note: a.note }) : t('remote.reason_assist', { code: fmtCode(a.code) });
}

// ---- 結果與下一步 ----

/** 下一步：note = 〔加備註後結束〕；retry = 〔再試一次〕；passkey = 去註冊 passkey；policy = 去組織安全政策；seats = 授權台數。 */
export type NextStep = 'note' | 'retry' | 'passkey' | 'policy' | 'seats' | 'none';

export interface Outcome {
  /** remote.out.<key>（標題）與 remote.out_next.<key>（下一步一句） */
  key: string;
  next: NextStep;
  /** 錯誤碼（小字，UX-04） */
  code?: string;
  /** 回到表單（passkey 被取消） */
  back?: boolean;
}

const NEXT: Record<string, NextStep> = {
  denied: 'note',
  reported: 'none',
  timeout: 'retry',
  no_user: 'none',
  locked: 'retry',
  reported_recently: 'none',
  region: 'none',
  allowance: 'none',
  org_policy: 'policy',
  paused: 'none',
  unsupported_desktop: 'none',
  role: 'none',
  busy: 'none',
  offline: 'retry',
  unlicensed: 'seats',
  taken: 'none',
  assist_expired: 'none',
  need_passkey: 'passkey',
  receipt: 'retry',
  too_many: 'none',
  forbidden: 'none',
  no_answer: 'retry',
  connect_failed: 'retry',
  connection_lost: 'retry',
  expired: 'retry',
  revoked: 'none',
  kill_switch: 'none',
  global_stop: 'none',
  error: 'retry',
  unlock_timeout: 'retry',
};

function outcome(key: string, code?: string): Outcome {
  return { key, next: NEXT[key] ?? 'retry', code };
}

const NOT_ALLOWED = new Set(['org_policy', 'allowance', 'role', 'region', 'no_user', 'locked', 'reported_recently', 'paused', 'unsupported_desktop']);

/** 建立 / 開始 / passkey 失敗 → 結果。passkey 視窗被取消 → 回表單（不算失敗）。 */
export function errorOutcome(e: unknown): Outcome {
  const name = (e as { name?: string })?.name;
  const code = (e as { code?: string })?.code;
  if (name === 'NotAllowedError' || name === 'AbortError' || code === 'PASSKEY_CANCELED') return { key: 'canceled', next: 'none', back: true };
  if (!isKinioError(e)) {
    // BFF / identity 的錯誤（session.ts SessionError）：沒有 passkey、驗不過
    if (code === 'PASSKEY_REQUIRED' || code === 'NO_PASSKEY' || code === 'PASSKEY_NOT_REGISTERED') return outcome('need_passkey', code);
    if (code === 'NETWORK') return outcome('error', code);
    return outcome('receipt', code || 'default');
  }
  const reason = typeof e.detail?.reason === 'string' ? e.detail.reason : '';
  switch (e.code) {
    case 'REMOTE_NOT_ALLOWED':
      return outcome(NOT_ALLOWED.has(reason) ? reason : 'allowance', e.code);
    case 'PASSKEY_REQUIRED':
      return outcome('need_passkey', e.code);
    case 'RECEIPT_INVALID':
      return outcome('receipt', e.code);
    case 'CONFLICT':
      if (reason === 'offline') return outcome('offline', e.code);
      if (reason === 'session_busy') return outcome('busy', e.code);
      return outcome('busy', e.code);
    case 'SEAT_UNLICENSED':
      return outcome('unlicensed', e.code);
    case 'ASSIST_REQUEST_TAKEN':
      return outcome('taken', e.code);
    case 'ASSIST_REQUEST_EXPIRED':
      return outcome('assist_expired', e.code);
    case 'RATE_LIMITED':
      return outcome('too_many', e.code);
    case 'FORBIDDEN':
      return outcome('forbidden', e.code);
    case 'KMGR_GLOBAL_STOP':
      return outcome('global_stop', e.code);
    case 'KMGR_STOPPED':
      return outcome('paused', e.code);
    case 'UPSTREAM':
    case 'UNAVAILABLE':
      return outcome('no_answer', e.code);
    case 'GONE':
    case 'REMOTE_CONSENT_TIMEOUT':
      return outcome('timeout', e.code);
    default:
      return outcome('error', e.code);
  }
}

/**
 * …/start 回 403 REMOTE_NOT_ALLOWED locked 而且帶 wait_unlock_until（kmgr M4-06b）：Agent 留著這次請求、對方解鎖就自己跳允許視窗。
 * 回等到幾點（ISO）；不是這種情況回 null（照一般錯誤畫結果）。
 */
export function waitUnlockOf(e: unknown): string | null {
  if (!isKinioError(e) || e.code !== 'REMOTE_NOT_ALLOWED' || e.detail?.reason !== 'locked') return null;
  const u = e.detail?.wait_unlock_until;
  return typeof u === 'string' && Date.parse(u) > Date.now() ? u : null;
}

/** 等過解鎖、最後被拒（等到期限都沒解鎖）→「〈姓名〉沒有在時間內解鎖」；其他照結束原因。 */
export function endOutcomeOf(s: Pick<RemoteSession, 'end_reason'>, events: RemoteEvent[] | undefined): Outcome | null {
  if (s.end_reason === 'rejected' && events?.some((e) => e.kind === 'waiting_unlock') && !events.some((e) => e.kind === 'unlocked')) return outcome('unlock_timeout', 'rejected');
  return endOutcome(s.end_reason);
}

/** 允許視窗送到主控台、另外還有幾個登入的工作階段（Agent 回報的 console 事件；沒有回 0）。 */
export function otherSessions(events: RemoteEvent[] | undefined): number {
  const ev = [...(events ?? [])].reverse().find((e) => e.kind === 'console');
  const n = Number(ev?.detail.other_sessions ?? 0);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/** 結束原因 → 結果；正常結束（操作者 / 員工按結束、時間到）回 null（畫結束畫面）。 */
export function endOutcome(reason: string | undefined): Outcome | null {
  switch (reason) {
    case 'operator_end':
    case 'user_end':
    case 'time_limit':
    case undefined:
    case '':
      return null;
    case 'denied':
    case 'reported':
    case 'timeout':
    case 'connect_failed':
    case 'connection_lost':
    case 'expired':
    case 'revoked':
    case 'kill_switch':
      return outcome(reason, reason);
    case 'rejected':
      return outcome('allowance', reason);
    default:
      return outcome('error', reason);
  }
}

/** 從來沒連上過的結束（拒絕、逾時、沒接上）不畫結束畫面的時長與檔案。 */
export function everActive(s: Pick<RemoteSession, 'active_at'>): boolean {
  return !!s.active_at;
}

// ---- 等待、倒數、加權限 ----

/** 從 since 起算的倒數（秒，不小於 0）。 */
export function secondsLeft(sinceMs: number, total: number, now = Date.now()): number {
  return Math.max(0, total - Math.floor((now - sinceMs) / 1000));
}

/** 秒 → 「0:58」。 */
export function clock(sec: number): string {
  const s = Math.max(0, Math.floor(sec));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/** kmgrctl 兌換過 launch code（kmgr 寫 launched 事件）= 這台電腦有遠端工具。 */
export function launched(events: RemoteEvent[] | undefined): boolean {
  return !!events?.some((e) => e.kind === 'launched');
}

/** 等待中的加權限 / 延長：最後一個 upgraded 事件多出來的權限與是否延長；不在等待就 null。 */
export function pendingUpgrade(s: Pick<RemoteSession, 'state' | 'permissions'>, events: RemoteEvent[] | undefined): { add: RemotePerm[]; extend: boolean; sinceMs: number } | null {
  if (s.state !== 'upgrade_pending') return null;
  const up = [...(events ?? [])].reverse().find((e) => e.kind === 'upgraded');
  if (!up) return { add: [], extend: false, sinceMs: Date.now() };
  const perms = Array.isArray(up.detail.permissions) ? (up.detail.permissions as string[]) : [];
  return {
    add: normalizePerms(perms).filter((p) => !s.permissions.includes(p)),
    extend: !!up.detail.extend,
    sinceMs: Date.parse(up.at),
  };
}

/** 等待允許的倒數起點：解鎖後才跳視窗的用 unlocked 事件，否則 started 事件（送出 session_open）的時間，沒有就用 started_at / 建立時間。 */
export function consentSince(s: Pick<RemoteSession, 'started_at' | 'created_at'>, events: RemoteEvent[] | undefined): number {
  const ev = [...(events ?? [])].reverse().find((e) => e.kind === 'unlocked') ?? events?.find((e) => e.kind === 'started');
  return Date.parse(ev?.at ?? s.started_at ?? s.created_at);
}

/** 走 TCP / TLS 中繼（relay_kind 3 / 4）時操作端角落寫「連線較慢」。 */
export function relaySlow(kind: number | undefined): boolean {
  return kind === 3 || kind === 4;
}

/** 還能延長嗎（從建立算總長 ≤ 4 小時、每次 + 30 分鐘）。 */
export function canExtend(s: Pick<RemoteSession, 'created_at' | 'ends_at'>): boolean {
  return Date.parse(s.ends_at) < Date.parse(s.created_at) + MAX_TOTAL_MS;
}

// ---- 結束畫面與紀錄 ----

export interface TransferredFile {
  direction: 'to_host' | 'from_host' | string;
  name: string;
  size: number;
  at: string;
}

export function filesOf(events: RemoteEvent[] | undefined): TransferredFile[] {
  return (events ?? [])
    .filter((e) => e.kind === 'file_transferred')
    .map((e) => ({
      direction: String(e.detail.direction ?? ''),
      name: String(e.detail.name ?? ''),
      size: Number(e.detail.size ?? 0),
      at: e.at,
    }));
}

/** 時長（秒）：ended 事件的 duration_seconds 優先；沒有就 結束（或現在）− 連上時間；沒連上過 = 0。 */
export function durationSeconds(s: Pick<RemoteSession, 'active_at' | 'ended_at' | 'duration_seconds'>, events?: RemoteEvent[], now = Date.now()): number {
  const ended = events?.find((e) => e.kind === 'ended');
  const d = ended?.detail.duration_seconds;
  if (typeof d === 'number') return d;
  if (typeof s.duration_seconds === 'number') return s.duration_seconds;
  if (!s.active_at) return 0;
  return Math.max(0, Math.round(((s.ended_at ? Date.parse(s.ended_at) : now) - Date.parse(s.active_at)) / 1000));
}

/** 時長一句（「23 分鐘」「1 小時 5 分鐘」「不到 1 分鐘」）。 */
export function durationText(t: TFn, sec: number): string {
  if (sec < 60) return t('remote.dur_lt_min');
  const m = Math.round(sec / 60);
  if (m < 60) return t('dur.minutes', { n: m });
  const h = Math.floor(m / 60);
  const rest = m % 60;
  return rest ? t('remote.dur_h_m', { h, m: rest }) : t('dur.hours', { n: h });
}

export function sizeText(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 ** 2) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 ** 3) return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
  return `${(bytes / 1024 ** 3).toFixed(2)} GB`;
}

/** 權限一句（「看、控制、檔案傳輸」）。 */
export function permsText(t: TFn, perms: string[]): string {
  return permsList(t, normalizePerms(perms));
}

/** 只列給的那幾項（加權限等待中：「檔案傳輸」，不補「看」）。 */
export function permsList(t: TFn, perms: string[]): string {
  return REMOTE_PERMS.filter((p) => perms.includes(p))
    .map((p) => t(`remote.perm.${p}`))
    .join(t('remote.list_sep'));
}

/** 預設期間：最近 30 天（含今天），回 yyyy-mm-dd（本地日期）。 */
export function defaultRange(now = new Date()): { from: string; to: string } {
  const d = (x: Date) => `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
  const from = new Date(now);
  from.setDate(from.getDate() - 29);
  return { from: d(from), to: d(now) };
}

/** 本地日期 yyyy-mm-dd → 期間的 [from, to) ISO（to 是那天的隔天 0 點）。 */
export function rangeIso(from: string, to: string): { from: string; to: string } {
  const f = new Date(from + 'T00:00:00');
  const t = new Date(to + 'T00:00:00');
  t.setDate(t.getDate() + 1);
  return { from: f.toISOString(), to: t.toISOString() };
}

export function inRange(s: Pick<RemoteSession, 'created_at'>, r: { from: string; to: string }): boolean {
  const at = Date.parse(s.created_at);
  return at >= Date.parse(r.from) && at < Date.parse(r.to);
}

/** CSV 一格：開頭是 = + - @ 的加 '（試算表公式注入），有逗號 / 引號 / 換行的加引號。 */
export function csvCell(v: unknown): string {
  const raw = v === undefined || v === null ? '' : String(v);
  const s = /^[=+\-@\t\r]/.test(raw) ? "'" + raw : raw;
  return /[",\n\r]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
}

export interface RecordRow {
  start: string;
  end: string;
  minutes: number | '';
  operator: string;
  org: string;
  home: string;
  host: string;
  permissions: string;
  result: string;
  reason: string;
  note: string;
  external_ticket: string;
}

/** 紀錄 CSV（UTF-8 BOM、CRLF，Excel 直接開中文不亂碼；欄位照 SPEC：期間、操作者、主機、時長、備註、外部工單）。 */
export function recordsCsv(rows: RecordRow[], header: string[]): string {
  const keys: (keyof RecordRow)[] = ['start', 'end', 'minutes', 'operator', 'org', 'home', 'host', 'permissions', 'result', 'reason', 'note', 'external_ticket'];
  const lines = [header.map(csvCell).join(',')];
  for (const r of rows) lines.push(keys.map((k) => csvCell(r[k])).join(','));
  return '﻿' + lines.join('\r\n') + '\r\n';
}

/** 結束原因的一句（紀錄表與 CSV）；還沒結束寫狀態。 */
export function resultText(t: TFn, s: Pick<RemoteSession, 'state' | 'end_reason'>): string {
  if (s.state !== 'ended') return t(`remote.state.${s.state}`);
  return t(`remote.end.${s.end_reason || 'unknown'}`, {
    defaultValue: t('remote.end.unknown'),
  });
}
